import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { ObjectException } from 'global/errors';

import { getCookie } from 'functions/getCookie';
import { useFreecodeContextRun, useFreecodeContextData } from 'hooks/useContext';
import { isNotValidObjectID } from 'functions/stringValidation';
import services from 'api/services';

const { ConfigService, FreecodeService } = services;

export default function useExerciseLoadFunction(exerciseId, courseId) {
  const { isInitialized } = useSelector((state) => state.auth);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(undefined);

  const { setTest, setTestImpl, setCourse, setLanguageData, setSponsor, setInput } = useFreecodeContextData();
  const { setLanguage, setCodeSol, setLevelToRun } = useFreecodeContextRun();

  useEffect(() => {
    if (!isInitialized || !exerciseId) return;
    const fetchData = async () => {
      try {
        if (isNotValidObjectID(exerciseId))
          throw new ObjectException(`Not a valid id ${exerciseId}`, 'VALIDATION_ERROR');
        if (courseId && isNotValidObjectID(courseId))
          throw new ObjectException(`Not a valid id ${courseId}`, 'VALIDATION_ERROR');

        const [languageVersions, response] = await Promise.all([
          ConfigService.getLanguageVersions(),
          FreecodeService.loadFreecodeTakeExercise(exerciseId, courseId),
        ]);
        const { test, testImpl, inputExercise, course, sponsor } = response;

        if (!test?._id) throw new ObjectException('Test missing from response', 'VALIDATION_ERROR');
        if (!testImpl?._id) throw new ObjectException('Implementation missing from response', 'VALIDATION_ERROR');
        if (test?._id !== testImpl?.test)
          throw new ObjectException('Test and implementation mismatch', 'VALIDATION_ERROR');

        let allowedLanguages = languageVersions?.value?.[testImpl?.executionVersion];
        if (!allowedLanguages?.python) throw new ObjectException('Language versions missing', 'VALIDATION_ERROR');

        // Find level to run
        let levelToRun = 0;
        if (localStorage.getItem(`fsEditor-exercise-${test._id}-levelToRun`))
          levelToRun = JSON.parse(localStorage.getItem(`fsEditor-exercise-${test._id}-levelToRun`));
        else if (inputExercise?.lastAutoSave) levelToRun = inputExercise?.lastAutoSave?.levelToRun;

        // Find language to use
        let foundLanguage =
          localStorage.getItem(`fsEditor-exercise-${test._id}-language`) ||
          inputExercise?.lastAutoSave?.language ||
          (allowedLanguages['python'] ? 'python' : Object.keys(allowedLanguages)[0]);
        if (course?._id) {
          const courseLanguage = getCookie(`fsCourse-${course._id}-language`);
          if (allowedLanguages[courseLanguage]) foundLanguage = courseLanguage;
        }

        const foundCode =
          localStorage.getItem(`fsEditor-exercise-${test._id}-${foundLanguage}`) ||
          inputExercise?.levelData?.[levelToRun]?.[foundLanguage]?.autoSave?.code ||
          testImpl.generated?.skeletons?.[foundLanguage] ||
          'Code not found...';
        localStorage.setItem(`fsEditor-exercise-${test._id}-${foundLanguage}`, foundCode);

        setInput(inputExercise);
        setTest(test);
        setTestImpl(testImpl);
        setLanguageData(allowedLanguages);
        setLanguage(foundLanguage);
        setCodeSol(foundCode);
        if (sponsor && !getCookie('isTicketUser')) setSponsor(sponsor);
        setLevelToRun(levelToRun);
        setCourse(course);

        setLoading(false);
      } catch (err) {
        setError(err);
      }
    };
    fetchData();
  }, [
    courseId,
    exerciseId,
    isInitialized,
    setCodeSol,
    setCourse,
    setInput,
    setLanguage,
    setLanguageData,
    setLevelToRun,
    setSponsor,
    setTest,
    setTestImpl,
  ]);

  return { loading, error };
}
